import { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { PopUpHandler } from '../../shared/PopupHandler';
import { MessageTabs, XtMessage, XtremePushGlobal } from './types';
import DOMPurify from 'dompurify';
import { filterSpecialHTMLCharacters, joinStrings } from '../../../utils/string';
import { Button } from '../../shared/Button';
import { Spinner } from '../../shared/Spinner';
import { ReactComponent as MessageStatusIcon } from './../../../assets/Itv/bell-message.svg';
import { FaChevronLeft } from '@react-icons/all-files/fa/FaChevronLeft';
import { getMessageCreateTime } from './helpers';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './ItvMessages.scss';

const MESSAGES_LOAD_STEP = 10;
const MESSAGES_COUNT_EDGE = 10;

export const ItvMessages: FC<{
  isOpen: boolean;
  msgCount?: number;
  setIsOpen: (arg: boolean) => void;
  setMsgCount: (arg: number) => void;
}> = ({ isOpen, setIsOpen, setMsgCount, msgCount }) => {
  const sanitize = DOMPurify.sanitize;
  const prevTab = useRef(MessageTabs.ALL);
  const { t } = useTranslations();

  const [messages, setMessages] = useState<XtMessage[]>([]);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [loadMoreLimit, setLoadMoreLimit] = useState<number>(MESSAGES_LOAD_STEP);
  const [isNewMsgFetching, setIsNewMsgFetching] = useState(false);
  const [isInitFetching, setIsInitFetching] = useState(true);
  const [allMsgCount, setAllMsgCount] = useState<number | undefined>();
  const [activeTab, setActiveTab] = useState<MessageTabs>(MessageTabs.ALL);
  const [msgOpened, setMsgOpened] = useState<{ id: number; shouldOpen: boolean } | null>();
  const [refetchMsgs, setRefetchMsgs] = useState(false);
  const [refetchMsgCount, setRefetchMsgCount] = useState(false);

  const reduceBadge = (resBadge: undefined | number): false | void => resBadge !== undefined && setMsgCount(resBadge);

  const handleCount = (count?: number): string | number =>
    Number(count) >= MESSAGES_COUNT_EDGE ? `${MESSAGES_COUNT_EDGE - 1}+` : String(count);

  const resetMessagesState = (): void => {
    setMessages([]);
    setLoadMoreLimit(MESSAGES_LOAD_STEP);
    setIsInitFetching(true);
  };

  const handleTabOpen = (tab: MessageTabs): void => {
    const isNotCurrentTab = activeTab !== tab;
    const isMsgOpenedAndTabActive = msgOpened && activeTab === tab;

    if (isNotCurrentTab || isMsgOpenedAndTabActive) {
      setActiveTab(tab);
      setMsgOpened(null);
      resetMessagesState();
    }

    // Open tab straight from message expanded view
    isMsgOpenedAndTabActive && setRefetchMsgs(!refetchMsgs);
  };

  const onBackClick = (): void => {
    setMsgOpened(null);

    if (msgOpened?.shouldOpen) {
      resetMessagesState();
      setRefetchMsgs(!refetchMsgs);
    }
  };

  const notFoundMsgs = useMemo(
    () => [
      {
        condition: activeTab === MessageTabs.ALL && !allMsgCount,
        text: t('messages.absent.all'),
      },
      {
        condition: activeTab === MessageTabs.UNREAD && !msgCount,
        text: t('messages.absent.unread'),
      },
      {
        condition: activeTab === MessageTabs.READ && !messages.length,
        text: t('messages.absent.read'),
      },
    ],
    [messages]
  );

  useEffect(() => {
    setIsNewMsgFetching(true);

    const isSameTab = prevTab.current === activeTab;
    const resultLimit = isSameTab ? loadMoreLimit : MESSAGES_LOAD_STEP;

    (window as unknown as XtremePushGlobal).xtremepush(
      'inbox',
      'message.list',
      {
        limit: MESSAGES_LOAD_STEP,
        offset: resultLimit - MESSAGES_LOAD_STEP,
        ...(activeTab !== MessageTabs.ALL && { opened: Number(activeTab === MessageTabs.READ) }),
      },
      (result) => {
        setMessages((prevMessages) =>
          prevTab.current === activeTab ? [...prevMessages, ...result.items] : result.items
        );
        setIsNewMsgFetching(false);
        setIsInitFetching(false);

        reduceBadge(result.badge);
        setShowLoadMore(result.more);
      },
      (error) => {
        setIsNewMsgFetching(false);
        console.log(error);
      }
    );
  }, [activeTab, loadMoreLimit, refetchMsgs]);

  useEffect(() => {
    (window as unknown as XtremePushGlobal).xtremepush(
      'inbox',
      'message.count',
      {},
      (result) => setAllMsgCount(Number(result.count)),
      (error) => console.log(error)
    );
  }, [refetchMsgCount]);

  useEffect(() => {
    msgOpened?.shouldOpen &&
      (window as unknown as XtremePushGlobal).xtremepush(
        'inbox',
        'message.action',
        { id: msgOpened.id, open: 1 },
        (result) => {
          setMsgCount(result.badge);
          setRefetchMsgCount(!refetchMsgCount);
        },
        (error) => console.log(error)
      );
  }, [msgOpened?.shouldOpen]);

  useEffect(() => {
    if (!isNewMsgFetching) {
      prevTab.current = activeTab;
    }
  }, [isNewMsgFetching]);

  const getMessageDot = (message: XtMessage, className?: string): boolean | ReactElement =>
    !(message.opened || msgOpened?.shouldOpen) && (
      <div className={joinStrings(['itv-messages__message-dot', className && `itv-messages__${className}`])} />
    );

  const isTitleContentPresent = (message: XtMessage): boolean => {
    const parser = new DOMParser();

    let isContentPresent = false;
    const titleDOM = parser.parseFromString(message.message.title, 'text/html');

    const titleNodes = titleDOM.querySelectorAll('*');

    titleNodes.forEach((node) => {
      if (node.textContent) isContentPresent = true;
    });

    return isContentPresent;
  };

  return (
    <PopUpHandler
      backdropClassName="itv-messages"
      isOpen={isOpen}
      header={
        (
          <div className="itv-messages__header">
            <div className="itv-messages__inner-header">
              {msgOpened && <FaChevronLeft className="itv-messages__chevron" onClick={onBackClick} />}
              {t('messages.title')}
              <div className="itv-messages__tabs">
                {Object.values(MessageTabs).map((mesageTab) => {
                  const isAllMsgTab = mesageTab === MessageTabs.ALL;
                  const isUnreadMsgTab = mesageTab === MessageTabs.UNREAD;
                  const isReadMsgTab = mesageTab === MessageTabs.READ;

                  return (
                    <div
                      key={mesageTab}
                      className={joinStrings([
                        'itv-messages__tab',
                        activeTab === mesageTab && 'itv-messages__tab--active',
                      ])}
                      onClick={(): void => handleTabOpen(mesageTab)}
                    >
                      {isAllMsgTab && t('messages.all')}
                      {isUnreadMsgTab && t('messages.unread')}
                      {isReadMsgTab && t('messages.read')}
                      <>
                        {isAllMsgTab && Boolean(allMsgCount) && (
                          <div className="itv-messages__message-counter">{handleCount(allMsgCount)}</div>
                        )}
                        {isUnreadMsgTab && Boolean(msgCount) && (
                          <div className="itv-messages__message-counter">{handleCount(msgCount)}</div>
                        )}
                      </>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) as unknown as string
      }
      onClose={(): void => setIsOpen(false)}
    >
      <div className="itv-messages__messages-section">
        {isInitFetching ? (
          <Spinner />
        ) : (
          <>
            {messages.map((message) => {
              const isTitle = isTitleContentPresent(message);

              return (
                (msgOpened ? msgOpened.id === message.id : true) && (
                  <div
                    key={message.id}
                    className={joinStrings([
                      'itv-messages__message-wrapper',
                      !message.opened && 'itv-messages__message-wrapper--unread',
                      msgOpened && 'itv-messages__message-wrapper--expanded',
                    ])}
                    onClick={(): void => setMsgOpened({ id: message.id, shouldOpen: !message.opened })}
                  >
                    <div
                      className={joinStrings([
                        'itv-messages__message-status-badge',
                        !message.opened && 'itv-messages__message-status-badge--unread',
                      ])}
                    >
                      <MessageStatusIcon className="itv-messages__message-status-icon" />
                    </div>
                    <div
                      className={joinStrings([
                        'itv-messages__message-content',
                        msgOpened && 'itv-messages__message-content--expanded',
                      ])}
                    >
                      <div className="itv-messages__message-title">
                        {isTitle && getMessageDot(message)}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitize(message.message.title)),
                          }}
                        />
                      </div>
                      <div
                        className={joinStrings([
                          'itv-messages__message-content',
                          msgOpened && 'itv-messages__message-content--expanded',
                        ])}
                      >
                        {!isTitle && getMessageDot(message, 'message-fallback')}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: filterSpecialHTMLCharacters(sanitize(message.message.alert)),
                          }}
                        />
                      </div>

                      <div className="itv-messages__message-time">
                        {getMessageCreateTime(message.create_time, t('messages.at'))}
                      </div>
                    </div>
                  </div>
                )
              );
            })}
            {showLoadMore && !msgOpened && (
              <Button
                className="itv-messages__more-btn"
                disabled={isNewMsgFetching}
                isSubmitting={isNewMsgFetching}
                variant="secondary"
                type="button"
                title="Load More"
                onClick={(): void => setLoadMoreLimit((prev) => prev + MESSAGES_LOAD_STEP)}
              >
                {t('messages.view-more')}
              </Button>
            )}
            {notFoundMsgs.map(
              (notFoundMsg) =>
                notFoundMsg.condition && (
                  <div key={notFoundMsg.text} className="itv-messages__not-found">
                    {notFoundMsg.text}
                  </div>
                )
            )}
          </>
        )}
      </div>
    </PopUpHandler>
  );
};
